import type { AdminSupportInquiryDetail } from '@inflip/contracts/admin';
import {
  ORDER_STATUS_LABELS,
  SUPPORT_INQUIRY_BODY_MAX,
  SUPPORT_INQUIRY_CATEGORY_LABELS,
  SUPPORT_INQUIRY_STATUS_LABELS,
  SupportInquiryStatus,
  SupportMessageAuthor,
} from '@inflip/shared';
import { Link, useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { SUPPORT_STATUS_TONE } from './support-inquiries-page';
import { BackLink } from '../components/back-link';
import {
  Badge,
  Button,
  ConfirmDialog,
  ErrorBanner,
  InfoRow,
  inputCls,
  LoadingRow,
  PageHeader,
  Section,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ORDER_STATUS_TEXT } from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { showToast } from '../lib/toast';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

/** 1:1 문의 상세 — 글 흐름, 답변 보내기, 문의 종료 */
export function SupportInquiryDetailPage() {
  const { session } = useAdminAuth();
  const { inquiryId } = useParams({ from: '/support/$inquiryId' });
  const [detail, setDetail] = useState<AdminSupportInquiryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .getSupportInquiry(session.accessToken, inquiryId)
      .then(setDetail)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, inquiryId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!session) return null;

  // 처리 뒤 — 성공이든 실패든 지금 상태를 다시 불러온다 (다른 관리자가 먼저 답했을 수 있다)
  async function run(action: () => Promise<unknown>, done: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      showToast(done);
      notifyPendingWorkChanged();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      await load();
      setBusy(false);
    }
  }

  async function handleReply() {
    if (!session || !reply.trim()) return;
    const ok = await run(
      () => api.replySupportInquiry(session.accessToken, inquiryId, reply.trim()),
      '답변을 보냈어요',
    );
    if (ok) setReply('');
  }

  async function handleClose() {
    if (!session) return;
    await run(() => api.closeSupportInquiry(session.accessToken, inquiryId), '문의를 종료했어요');
    setConfirmClose(false);
  }

  const closed = detail?.status === SupportInquiryStatus.CLOSED;

  return (
    <main className="w-full px-6 py-8">
      <div className="mb-2">
        <BackLink
          fallback="/support"
          fallbackLabel="1:1 문의"
          className="text-sm font-medium text-brand hover:underline"
        />
      </div>
      <PageHeader
        title={detail?.title ?? '1:1 문의'}
        actions={
          detail &&
          !closed && (
            <Button variant="secondary" disabled={busy} onClick={() => setConfirmClose(true)}>
              문의 종료
            </Button>
          )
        }
      />

      {error && <ErrorBanner message={error} />}
      {!detail && !error && <LoadingRow />}

      {detail && (
        <div className="flex flex-col gap-6 lg:flex-row">
          <section className="min-w-0 flex-1">
            <ol className="flex flex-col gap-3">
              {detail.messages.map((m) => {
                const admin = m.author === SupportMessageAuthor.ADMIN;
                return (
                  <li
                    key={m.id}
                    className={`rounded-lg px-4 py-3 ${admin ? 'ml-10 bg-brand-soft' : 'mr-10 bg-muted/60'}`}
                  >
                    <p className="text-xs font-semibold text-muted-foreground">
                      {admin ? '관리자' : '회원'} · {m.authorName ?? '이름 없음'}
                      <span className="ml-2 font-normal">{formatDateTime(m.createdAt)}</span>
                    </p>
                    <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-relaxed">
                      {m.body}
                    </p>
                  </li>
                );
              })}
            </ol>

            {closed ? (
              <p className="mt-5 rounded-lg bg-muted/60 px-4 py-3 text-sm text-muted-foreground">
                종료된 문의예요{detail.closedAt ? ` (${formatDateTime(detail.closedAt)})` : ''}.
                {detail.userDeletedAt
                  ? ` 회원이 ${formatDateTime(detail.userDeletedAt)}에 삭제해 회원 화면에서는 보이지 않아요.`
                  : ' 회원이 더 물으려면 새 문의를 남겨요.'}
              </p>
            ) : (
              <form
                className="mt-5 flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleReply();
                }}
              >
                <textarea
                  className={`h-32 w-full resize-y ${inputCls}`}
                  placeholder="답변을 적어 주세요. 보내면 회원에게 알림이 가요."
                  value={reply}
                  maxLength={SUPPORT_INQUIRY_BODY_MAX}
                  onChange={(e) => setReply(e.target.value)}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {reply.length.toLocaleString('ko-KR')} /{' '}
                    {SUPPORT_INQUIRY_BODY_MAX.toLocaleString('ko-KR')}
                  </span>
                  <Button type="submit" disabled={busy || !reply.trim()}>
                    답변 보내기
                  </Button>
                </div>
              </form>
            )}
          </section>

          <aside className="w-full shrink-0 lg:w-80">
            <Section title="문의 정보">
              <InfoRow label="상태">
                <span className="flex flex-wrap items-center gap-1">
                  <Badge tone={SUPPORT_STATUS_TONE[detail.status]}>
                    {SUPPORT_INQUIRY_STATUS_LABELS[detail.status]}
                  </Badge>
                  {detail.userDeletedAt && <Badge>회원 삭제</Badge>}
                </span>
              </InfoRow>
              <InfoRow label="분류">{SUPPORT_INQUIRY_CATEGORY_LABELS[detail.category]}</InfoRow>
              <InfoRow label="회원">
                <Link
                  to="/users/$userId"
                  params={{ userId: detail.user.id }}
                  className="text-brand hover:underline"
                >
                  {detail.user.name ?? '(이름 없음)'}
                </Link>
                {detail.user.email && (
                  <span className="ml-1 text-xs text-muted-foreground">{detail.user.email}</span>
                )}
              </InfoRow>
              <InfoRow label="주문">
                {detail.order ? (
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: detail.order.id }}
                    className="text-brand hover:underline"
                  >
                    {detail.order.listingTitle ?? `#${detail.order.id.slice(0, 8)}`}
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({detail.order.role === 'buyer' ? '구매자' : '판매자'} ·{' '}
                      <span className={ORDER_STATUS_TEXT[detail.order.status]}>
                        {ORDER_STATUS_LABELS[detail.order.status]}
                      </span>
                      )
                    </span>
                  </Link>
                ) : (
                  '—'
                )}
              </InfoRow>
              <InfoRow label="남긴 때">{formatDateTime(detail.createdAt)}</InfoRow>
              <InfoRow label="마지막 답변">
                {detail.answeredAt ? formatDateTime(detail.answeredAt) : '—'}
              </InfoRow>
            </Section>
          </aside>
        </div>
      )}

      {confirmClose && (
        <ConfirmDialog
          title="문의를 종료할까요?"
          description="종료하면 회원도 관리자도 이 문의에 더 쓸 수 없어요. 회원은 새 문의로 다시 물을 수 있어요."
          confirmLabel="종료하기"
          busy={busy}
          onConfirm={() => void handleClose()}
          onClose={() => setConfirmClose(false)}
        />
      )}
    </main>
  );
}
